import { ComponentType, useMemo } from "react";
import { IVerticalTabProps } from "@miplatform/types";
import { VerticalTabsLayout } from "../VerticalTabsLayout";

export function FilteredDataVerticalTabsLayout<GenericData>(
  props: Readonly<IFilteredDataVerticalTabsLayoutProps<GenericData>>
) {
  const {
    data = [],
    showAllOption = false,
    allOptionLabel = "All",
    alphabeticalOrder = false,
    viewComponent,
    extraTabProps,
    filteredKeyExtractor,
    onTabChange,
    extraLayoutProps,
    useLegacyStyles,
    hideNavigationButtons = true,
  } = props;

  const uniqueFilterValues = Array.from(
    new Set(data.map((item) => filteredKeyExtractor(item)))
  );

  const tabs: IVerticalTabProps<GenericDataList<GenericData>>[] =
    useMemo(() => {
      const generatedTabs = Array.from(uniqueFilterValues.values()).map(
        (item) => ({
          label: item,
          value: item,
          data: data.filter((d) => filteredKeyExtractor(d) === item),
          viewComponent,
          extraTabProps,
        })
      );

      if (showAllOption) {
        generatedTabs.unshift({
          label: allOptionLabel,
          value: "all",
          data,
          viewComponent,
          extraTabProps,
        });
      }

      if (alphabeticalOrder) {
        generatedTabs.sort((a, b) => a.label.localeCompare(b.label));
      }

      return generatedTabs;
    }, [data, filteredKeyExtractor]);

  return (
    <VerticalTabsLayout
      tabs={tabs}
      hideStatusIcons
      onTabChange={onTabChange}
      extraLayoutProps={extraLayoutProps}
      useLegacyStyles={useLegacyStyles}
      hideNavigationButtons={hideNavigationButtons}
    />
  );
}

interface IFilteredDataVerticalTabsLayoutProps<GenericData> {
  data: GenericDataList<GenericData>;
  showAllOption?: boolean;
  alphabeticalOrder?: boolean;
  allOptionLabel?: string;
  viewComponent: ComponentType<IVerticalTabProps<GenericDataList<GenericData>>>;
  extraTabProps?: Record<string, unknown>;
  filteredKeyExtractor: (item: GenericData) => string;
  onTabChange?: (tab?: IVerticalTabProps<GenericDataList<GenericData>>) => void;
  extraLayoutProps?: Record<string, unknown>;
  useLegacyStyles?: boolean;
  hideNavigationButtons?: boolean;
}

type GenericDataList<GenericData> = GenericData[];
